// src/components/ProfilePhotoUpload.js
import React, { useState, useCallback } from "react";
import { Button, Snackbar, Slider } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import Cropper from "react-easy-crop";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { useAuth } from "../../context/AuthContext";

// Create an Alert component for the Snackbar.
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Helper functions for cropping
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    // Allow cross-origin if necessary:
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error("Canvas is empty");
        reject(new Error("Canvas is empty"));
        return;
      }
      blob.name = "cropped.jpeg";
      resolve(blob);
    }, "image/jpeg");
  });
};

const ProfilePhotoUpload = ({ currentPhotoURL, onUploadSuccess }) => {
  const { user } = useAuth();
  const [photoFile, setPhotoFile] = useState(null);
  const [previewURL, setPreviewURL] = useState("");
  // For cropping UI:
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Handle file selection and generate a preview URL. Show cropper UI.
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Set the original file until cropped.
      setPhotoFile(file);
      const url = URL.createObjectURL(file);
      setPreviewURL(url);
      setShowCropper(true);
    }
  };

  // When the crop completes, save the cropping coordinates.
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Handle cropping: generate a new blob and update previewURL.
  const handleCrop = async () => {
    try {
      const croppedBlob = await getCroppedImg(previewURL, croppedAreaPixels);
      setPhotoFile(croppedBlob);
      const newPreviewURL = URL.createObjectURL(croppedBlob);
      setPreviewURL(newPreviewURL);
      setShowCropper(false);
    } catch (error) {
      console.error("Error during cropping:", error);
    }
  };

  // Handle photo upload, update Firestore, notify parent, reset state, and show Snackbar.
  const handlePhotoUpload = async () => {
    if (!photoFile) return;

    try {
      const storage = getStorage();
      // Define a storage reference under a user-specific folder.
      const photoRef = ref(storage, `profilePhotos/${user.uid}/photo.jpg`);
      await uploadBytes(photoRef, photoFile);
      const downloadURL = await getDownloadURL(photoRef);

      // Update the user document in Firestore with the new photo URL.
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, { photoURL: downloadURL });

      // Notify the parent component about the successful upload.
      onUploadSuccess(downloadURL);

      // Reset state.
      setPhotoFile(null);
      setPreviewURL("");
      setShowCropper(false);

      // Show the success Snackbar.
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to upload photo:", error);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <div style={{ textAlign: "center", marginBottom: "16px" }}>
      {/* Show cropper UI if activated */}
      {showCropper && previewURL ? (
        <div style={{ position: "relative", width: "150px", height: "150px", margin: "0 auto" }}>
          <Cropper
            image={previewURL}
            crop={crop}
            zoom={zoom}
            aspect={1}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
          <div style={{ marginTop: "8px" }}>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, z) => setZoom(z)}
              aria-labelledby="Zoom"
              style={{ width: "150px", margin: "0 auto" }}
            />
          </div>
          <div style={{ marginTop: "8px" }}>
            <Button variant="outlined" onClick={handleCrop}>
              Crop & Use Photo
            </Button>
            <Button variant="outlined" onClick={() => setShowCropper(false)} sx={{ ml: 2 }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : previewURL ? (
        <img
          src={previewURL}
          alt="Profile Preview"
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      ) : currentPhotoURL ? (
        <img
          src={currentPhotoURL}
          alt="Profile"
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      ) : (
        <div
          style={{
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            backgroundColor: "#ccc",
            display: "inline-block",
            lineHeight: "150px",
            textAlign: "center",
            fontSize: "1rem",
            color: "#666",
          }}
        >
          No Photo
        </div>
      )}
      <div style={{ marginTop: "8px" }}>
        {/* Hidden file input and custom label for 'Choose Photo' */}
        <input
          accept="image/*"
          id="photo-upload"
          type="file"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <label htmlFor="photo-upload">
          <Button variant="outlined" component="span" sx={{ mt: 1 }}>
            Choose Photo
          </Button>
        </label>
        {/* Only show "Upload Photo" button if a file has been chosen and cropping is done */}
        {photoFile && !showCropper && (
          <Button variant="outlined" onClick={handlePhotoUpload} sx={{ mt: 1, ml: 2 }}>
            Upload Photo
          </Button>
        )}
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          sx={{
            width: "100%",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          Photo uploaded successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProfilePhotoUpload;